import { assertUnreachable } from "@aprao/common/src/assertUnreachable.js";
import { FRACTION_ZERO } from "@aprao/common/src/math/fraction.js";
import { mapForNullable } from "option-t/nullable";

import type { ShapeAppraisalDocument } from "../shapes/ShapeAppraisal.js";
import type { ShapeSalesTaxConfiguration } from "../shapes/ShapeProject.js";
import { AppraisalDocumentFacade } from "./AppraisalDocumentFacade.js";
import { CashflowFacade } from "./CashflowFacade.js";
import { FinanceFacade } from "./FinanceFacade.js";
import { FlowFacade, type FlowScheduler } from "./FlowFacade.js";
import { ResidualFacade } from "./ResidualFacade.js";
import { SalesTaxConfigurationFacade } from "./SalesTaxConfigurationFacade.js";
import {
  OverridableSitePriceManager,
  type SitePriceManager,
  SitePriceManagerImpl,
} from "./SitePriceManager.js";
import { TaxFacade } from "./TaxFacade.js";
import { TimelineFacade } from "./timeline/TimelineFacade.js";

type SitePriceManagerFactory<TSitePriceManager extends SitePriceManager> = (
  appraisalDocument: AppraisalDocumentFacade,
  salesTaxConfiguration: SalesTaxConfigurationFacade | null,
) => TSitePriceManager;

export const defaultPriceManagerFactory: SitePriceManagerFactory<
  SitePriceManagerImpl
> = (documentFacade, taxConfigurationFacade) =>
  new SitePriceManagerImpl(documentFacade, taxConfigurationFacade);

export const overridablePriceManagerFactory: SitePriceManagerFactory<
  OverridableSitePriceManager
> = (documentFacade, taxConfigurationFacade) =>
  new OverridableSitePriceManager(
    defaultPriceManagerFactory(documentFacade, taxConfigurationFacade),
  );

export const createAppraisalFacades = <
  TSitePriceManager extends SitePriceManager,
>(params: {
  document: ShapeAppraisalDocument;
  taxConfiguration: ShapeSalesTaxConfiguration | null;
  priceManagerFactory: SitePriceManagerFactory<TSitePriceManager>;
}) => {
  const _priceManagerFactory =
    params.priceManagerFactory ?? defaultPriceManagerFactory;

  const documentFacade = new AppraisalDocumentFacade(params.document);
  const taxConfigurationFacade = mapForNullable(
    params.taxConfiguration,
    (configuration) => new SalesTaxConfigurationFacade(configuration),
  );

  const priceManager = _priceManagerFactory(
    documentFacade,
    taxConfigurationFacade,
  );

  const residualFacade = new ResidualFacade({
    documentFacade,
    taxConfigurationFacade,
    priceManager,
  });

  const taxFacade = new TaxFacade(
    {
      documentFacade,
      taxConfigurationFacade,
    },
    {
      sitePurchasesPriceTotal: () => residualFacade.sitePurchasesPriceTotal,
    },
  );

  const financeFacade = new FinanceFacade({
    documentFacade,
    taxFacade,
    residualFacade,
  });

  const cashflowFacade = new CashflowFacade({
    documentFacade,
    taxFacade,
    priceManager,
    residualFacade,
  });

  const timelineFacade = new TimelineFacade({
    documentFacade,
    residualFacade,
    financeFacade,
    priceManager,
  });

  const flowScheduler: FlowScheduler =
    documentFacade.experimental_flowKind === "cashflow" ?
      {
        get duration() {
          return cashflowFacade.projectionDuration;
        },
        getNetCashflowsAtIndex: (index) =>
          cashflowFacade.getNetCashFlowsProjectionAtPeriodIndex(index),
      }
    : documentFacade.experimental_flowKind === "timeline" ?
      {
        get duration() {
          return timelineFacade.computedFrameRange.upper ?? 0;
        },
        getNetCashflowsAtIndex: (index: number) => ({
          debtFunded: timelineFacade.getNetCashflowAtFrame(index).amount,
          forwardFunded: FRACTION_ZERO,
        }),
      }
    : assertUnreachable(documentFacade.experimental_flowKind);

  const flowFacade = new FlowFacade({
    documentFacade,
    taxFacade,
    residualFacade,
    financeFacade,
    flowScheduler,
  });

  return {
    documentFacade,
    taxConfigurationFacade,
    taxFacade,
    cashflowFacade,
    timelineFacade,
    priceManager,
    residualFacade,
    financeFacade,
    flowFacade,
  };
};
